if (!window.__swiperLoaderInit) {
  window.__swiperLoaderInit = true;

  function loadSwiperAssets() {
    const loaderElement = document.querySelector("[data-swiper-script]");
    if (!loaderElement) return;

    const swiperCSSURL = loaderElement.dataset.swiperCss;
    if (swiperCSSURL && !document.querySelector(`link[href="${swiperCSSURL}"]`)) {
      const swiperCSS = document.createElement("link");
      swiperCSS.rel = "stylesheet";
      swiperCSS.href = swiperCSSURL;
      document.head.prepend(swiperCSS);
    }

    if (window.Swiper) {
      declareSwiperCustomElement();
      return;
    }

    const swiperScriptURL = loaderElement.dataset.swiperScript;
    if (!swiperScriptURL) return;

    const existingScript = document.querySelector(`script[src="${swiperScriptURL}"]`);
    if (existingScript) {
      if (window.Swiper) {
        declareSwiperCustomElement();
      } else {
        existingScript.addEventListener("load", declareSwiperCustomElement, { once: true });
      }
      return;
    }

    const swiperScript = document.createElement("script");
    swiperScript.src = swiperScriptURL;
    swiperScript.async = true;
    swiperScript.onload = declareSwiperCustomElement;
    document.head.appendChild(swiperScript);
  }

  document.addEventListener("DOMContentLoaded", () => {
    if (Shopify.designMode || document.querySelector("swiper-slider") || document.querySelector("[data-swiper-script]")) {
      loadSwiperAssets();
    }
  });

  function declareSwiperCustomElement() {
    if (customElements.get("swiper-slider")) return;

    class SwiperSlider extends HTMLElement {
      constructor() {
        super();
        this.slideContainer = null;
        this.slides = [];
        this.isVideoWrapper = null;
        this.sliderOptions = {};
        this.activeIndex = 0;
        this.initialized = false;
        this.resizeBound = false;
        this.resizeTimeout = null;
        this.loopCapacityTimeout = null;
        this.loopCapacityInProgress = false;
        this.thumbsBaseOptions = null;
        this.loopFixAttached = false;
        this.paginationProgressEnabled = this.dataset.tdPaginationProgress === "true";
        this.paginationProgressFrame = null;
        this.thumbnails = null;
        this.contentObserver = null;
        this.customListenersAttached = false;
        this.boundInitializeSwiper = this.initializeSwiper.bind(this);
        this.boundSlideToIndex = this.slideToIndex.bind(this);
        this.boundUpdateSlides = this.updateSlides.bind(this);
        this.boundHandleResize = this.handleResize.bind(this);
      }

      connectedCallback() {
        this.syncElements();
        this.addEventListener("update-slides", this.boundUpdateSlides);
        if (this.dataset.customEvent) {
          this.attachCustomListeners(this.dataset.customEvent);
        }
        this.setupContentObserver();
        if (this.slideContainer && this.slides.length > 0) {
          this.initializeSwiper();
        } else {
          this.waitForSlides();
        }
      }

      attachCustomListeners(customEvent) {
        if (this.customListenersAttached) return;

        switch (customEvent) {
          case "reinitialize":
            this.addEventListener("reinitialize-swiper", this.boundInitializeSwiper);
            break;
          case "slide-to-index":
            document.addEventListener("slide-to-index", this.boundSlideToIndex);
            break;
        }

        this.customListenersAttached = true;
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.boundHandleResize);
        this.removeEventListener("update-slides", this.boundUpdateSlides);
        clearTimeout(this.resizeTimeout);
        clearTimeout(this.loopCapacityTimeout);
        if (this.paginationProgressEnabled) {
          this.resetPaginationProgress();
        }
        this.cancelPaginationProgressFrame();
        if (this.contentObserver) {
          this.contentObserver.disconnect();
          this.contentObserver = null;
        }
        if (this.dataset.customEvent) {
          switch (this.dataset.customEvent) {
            case "reinitialize":
              this.removeEventListener("reinitialize-swiper", this.boundInitializeSwiper);
              break;
            case "slide-to-index":
              document.removeEventListener("slide-to-index", this.boundSlideToIndex);
              break;
          }
        }
        this.customListenersAttached = false;
      }

      // Rebuy/Vue-backed sliders can stamp their slide markup after the custom element is constructed,
      // so we resolve the live DOM right before init and whenever the subtree changes.
      syncElements() {
        this.slideContainer = this.querySelector("[data-swiper]:not([data-swiper-thumbs])");
        this.slides = Array.from(this.querySelectorAll("[data-swiper-slide]"));
        this.isVideoWrapper = this.querySelector("[data-video-wrapper]");
        this.sliderOptions = this.applyDefaultOptions(this.getSliderOptions());
        this.thumbnails = this.dataset.swiperThumbs ? this.querySelector("[data-swiper-thumbs]") : null;
      }

      setupContentObserver() {
        if (this.contentObserver) return;
        this.contentObserver = new MutationObserver(() => {
          this.syncElements();

          if (!this.slideContainer || this.slides.length === 0) {
            return;
          }
          
          // Delay first init until dynamic renderers have actually inserted the Swiper structure.
          if (!this.initialized) {
            this.initializeSwiper();
            return;
          }

          // Keep Swiper measurements in sync when a dynamic integration swaps or appends slides.
          this.slider.update?.();
          this.queueLoopCapacityCheck(50);
        });

        this.contentObserver.observe(this, {
          childList: true,
          subtree: true,
        });
      }

      waitForSlides() {
        this.syncElements();
        if (this.slideContainer && this.slides.length > 0) {
          this.initializeSwiper();
        }
      }

      applyDefaultOptions(options) {
        if (options.loop === undefined) {
          options.loop = true;
        }
        if (options.speed === undefined) {
          options.speed = 400;
        }
        return options;
      }

      getSliderOptions() {
        try {
          const options = this.dataset.swiperOptions;
          if (!options || options === "undefined") {
            return {};
          }
          return JSON.parse(options);
        } catch (err) {
          console.error(err);
          return {};
        }
      }

      resolveScopedElement(selector) {
        if (typeof selector !== "string") return selector;
        return this.querySelector(selector);
      }

      // Swiper resolves selector strings globally. Scope them to this slider instance so repeated
      // controls like `.swiper-scrollbar` inside drawers/widgets do not bind to the wrong slider.
      scopeControlElements(options) {
        if (!options || typeof options !== "object") return options;

        if (options.scrollbar?.el) {
          options.scrollbar.el = this.resolveScopedElement(options.scrollbar.el);
        }

        if (options.pagination?.el) {
          options.pagination.el = this.resolveScopedElement(options.pagination.el);
        }

        if (options.navigation) {
          if (options.navigation.nextEl) {
            options.navigation.nextEl = this.resolveScopedElement(options.navigation.nextEl);
          }
          if (options.navigation.prevEl) {
            options.navigation.prevEl = this.resolveScopedElement(options.navigation.prevEl);
          }
        }

        return options;
      }

      initializeSwiper() {
        this.syncElements();
        if (window.Swiper && !this.initialized) {
          this.initialized = true;
          const swiperWrapper = this;
          const shouldHandlePaginationProgress = this.paginationProgressEnabled;
          const originalEvents = this.sliderOptions.on || {};
          this.scopeControlElements(this.sliderOptions);
          if (this.thumbnails) {
            this.setThumbnails();
          }
          this.sliderOptions.on = {
            ...originalEvents,
            slideChange: function () {
              document.dispatchEvent(
                new CustomEvent("thumbnail-changed", {
                  detail: {
                    slide: this.slides[this.activeIndex],
                  },
                })
              );
              swiperWrapper.syncThumbnailPressedState();
              if (typeof originalEvents.slideChange === "function") {
                originalEvents.slideChange.apply(this, arguments);
              }
            },
            afterInit: function () {
              swiperWrapper.dispatchEvent(new CustomEvent("swiper:init", { bubbles: true }));
              swiperWrapper.syncThumbnailPressedState();
              if (typeof originalEvents.afterInit === "function") {
                originalEvents.afterInit.apply(this, arguments);
              }
              swiperWrapper.queueLoopCapacityCheck();
              if (this.thumbs) {
                swiperWrapper.dispatchEvent(
                  new CustomEvent("swiper:thumbs-ready", {
                    bubbles: true,
                    detail: {
                      mainSlider: this,
                      thumbsSlider: this.thumbs?.swiper,
                      thumbsBaseOptions: swiperWrapper.thumbsBaseOptions,
                    },
                  })
                );
              }
              if (shouldHandlePaginationProgress) {
                swiperWrapper.resetPaginationProgress();
              }
            },
            autoplayTimeLeft: function (swiperInstance, timeLeft, progress) {
              if (shouldHandlePaginationProgress) {
                swiperWrapper.updatePaginationProgress(progress);
              }
              if (typeof originalEvents.autoplayTimeLeft === "function") {
                originalEvents.autoplayTimeLeft.apply(this, arguments);
              }
            },
          };
          this.slider = new Swiper(this.slideContainer, this.sliderOptions);

          if (this.sliderOptions.loop && this.sliderOptions.centeredSlides && this.sliderOptions.slidesPerView === "auto") {
            this.slider.slideToLoop(0, 0, false);
          }

          this.queueLoopCapacityCheck();
          if (this.sliderOptions.loop && this.sliderOptions.centeredSlides) {
            this.queueLoopCapacityCheck(80);
          }
          this.initializeSliderEventListeners();
          this.bindResizeEvent();
        } else if (!window.Swiper) {
          console.warn("SwiperSlider - Swiper.js not found");
        }
      }

      bindResizeEvent() {
        if (!this.resizeBound) {
          window.addEventListener("resize", this.boundHandleResize);
          this.resizeBound = true;
        }
      }

      handleResize() {
        if (this.slider) {
          clearTimeout(this.resizeTimeout);
          this.resizeTimeout = setTimeout(() => {
            this.slider.updateSize();
            this.queueLoopCapacityCheck(50);
          }, 300);
        }
      }

      queueLoopCapacityCheck(delay = 0) {
        clearTimeout(this.loopCapacityTimeout);
        this.loopCapacityTimeout = setTimeout(() => {
          this.ensureLoopCapacity();
        }, delay);
      }

      getLoopCapacitySlides(targetSlider = this.slider) {
        if (!targetSlider?.wrapperEl) return [];
        return Array.from(targetSlider.wrapperEl.children).filter(
          (slide) =>
            slide.classList.contains("swiper-slide") && !slide.classList.contains("swiper-slide-duplicate")
        );
      }

      calculateSlidesTotalWidth(slides) {
        return slides.reduce((sum, slide) => {
          const rectWidth = slide.getBoundingClientRect().width;
          const style = window.getComputedStyle(slide);
          const marginLeft = parseFloat(style.marginLeft) || 0;
          const marginRight = parseFloat(style.marginRight) || 0;
          return sum + rectWidth + marginLeft + marginRight;
        }, 0);
      }

      attachLoopFixListener(slider, attachedKey = "loopFixAttached") {
        if (this[attachedKey] || !slider?.params?.loop || !slider.params?.centeredSlides) return;
        this[attachedKey] = true;
        slider.on("slideChangeTransitionEnd", () => {
          if (slider.destroyed || !slider.params?.loop) return;
          if (typeof slider.loopFix === "function") {
            requestAnimationFrame(() => {
              if (slider.destroyed) return;
              slider.loopFix();
              slider.updateSlidesClasses?.();
            });
          }
        });
      }

      getRequiredLoopWidth(containerWidth, slidesWidth, slideCount) {
        const widthPerSlide = slideCount > 0 ? slidesWidth / slideCount : 0;
        return containerWidth + widthPerSlide;
      }

      getBreakpointLoopParams(containerWidth) {
        const opts = this.sliderOptions || {};
        const breakpoints = opts.breakpoints || {};
        const breakpointWidth =
          opts.breakpointsBase === "container"
            ? containerWidth
            : typeof window !== "undefined"
              ? window.innerWidth
              : containerWidth;
        const keys = Object.keys(breakpoints)
          .map(Number)
          .filter((k) => !Number.isNaN(k) && k <= breakpointWidth)
          .sort((a, b) => b - a);
        const active = keys[0] != null ? breakpoints[String(keys[0])] : {};
        return {
          loop: active.loop ?? opts.loop ?? false,
          centeredSlides: active.centeredSlides ?? opts.centeredSlides ?? false,
          centerInsufficientSlides: active.centerInsufficientSlides ?? opts.centerInsufficientSlides ?? false,
        };
      }

      restoreAutoplayState() {
        if (!this.slider || this.slider.destroyed || !this.slider.params?.autoplay || !this.slider.autoplay) return;

        if (this.slider.autoplay.paused && typeof this.slider.autoplay.resume === "function") {
          this.slider.autoplay.resume();
          return;
        }

        if (!this.slider.autoplay.running && typeof this.slider.autoplay.start === "function") {
          this.slider.autoplay.start();
        }
      }

      cancelPaginationProgressFrame() {
        if (this.paginationProgressFrame === null) return;
        cancelAnimationFrame(this.paginationProgressFrame);
        this.paginationProgressFrame = null;
      }

      resetPaginationProgress() {
        this.cancelPaginationProgressFrame();
        this.style.setProperty("--td-swiper-progress", "0");
      }

      updatePaginationProgress(swiperProgress = 1) {
        const normalizedProgress = Math.min(Math.max(1 - Number(swiperProgress), 0), 1);
        this.setPaginationProgress(Number.isNaN(normalizedProgress) ? 0 : normalizedProgress);
      }

      setPaginationProgress(progress = 0) {
        this.cancelPaginationProgressFrame();
        this.paginationProgressFrame = requestAnimationFrame(() => {
          this.paginationProgressFrame = null;
          this.style.setProperty("--td-swiper-progress", String(progress));
        });
      }

      ensureLoopCapacity() {
        this.applyLoopOrCenterInsufficientSlides(this.slider, (w) => this.getBreakpointLoopParams(w), {
          inProgressKey: "loopCapacityInProgress",
          loopFixAttachedKey: "loopFixAttached",
        });
      }

      applyLoopOrCenterInsufficientSlides(targetSlider, baseParamsOrGetter, options = {}) {
        const { inProgressKey, loopFixAttachedKey = "loopFixAttached", modifyOriginalParams = false, onComplete } = options;
        if (!targetSlider || targetSlider.destroyed || this[inProgressKey]) return;

        const containerWidth = targetSlider.width || targetSlider.el?.clientWidth || 0;
        if (!containerWidth) return;

        const baseParams =
          typeof baseParamsOrGetter === "function" ? baseParamsOrGetter(containerWidth) : baseParamsOrGetter;
        if (!baseParams?.loop || !baseParams?.centeredSlides) return;

        const sourceSlides = this.getLoopCapacitySlides(targetSlider);
        if (!sourceSlides.length) return;

        const slidesWidth = this.calculateSlidesTotalWidth(sourceSlides);
        const requiredWidth = this.getRequiredLoopWidth(containerWidth, slidesWidth, sourceSlides.length);
        const shouldUseCenterInsufficientSlides = slidesWidth < requiredWidth;
        const desiredLoop = shouldUseCenterInsufficientSlides ? false : baseParams.loop;
        const desiredCenteredSlides = shouldUseCenterInsufficientSlides ? false : baseParams.centeredSlides;
        const desiredCenterInsufficientSlides = shouldUseCenterInsufficientSlides
          ? true
          : baseParams.centerInsufficientSlides;

        const hasModeChanges =
          targetSlider.params.loop !== desiredLoop ||
          targetSlider.params.centeredSlides !== desiredCenteredSlides ||
          targetSlider.params.centerInsufficientSlides !== desiredCenterInsufficientSlides;
        if (!hasModeChanges) return;

        const activeRealIndex = targetSlider.realIndex ?? 0;
        this[inProgressKey] = true;

        try {
          if (targetSlider.params.loop && !desiredLoop && typeof targetSlider.loopDestroy === "function") {
            targetSlider.loopDestroy();
          }

          targetSlider.params.loop = desiredLoop;
          targetSlider.params.centeredSlides = desiredCenteredSlides;
          targetSlider.params.centerInsufficientSlides = desiredCenterInsufficientSlides;

          if (modifyOriginalParams && targetSlider.originalParams) {
            targetSlider.originalParams.loop = desiredLoop;
            targetSlider.originalParams.centeredSlides = desiredCenteredSlides;
            targetSlider.originalParams.centerInsufficientSlides = desiredCenterInsufficientSlides;
          }

          targetSlider.updateSlides();
          targetSlider.updateSize();
          if (desiredLoop) {
            targetSlider.params.loopedSlides = sourceSlides.length;
          }
          if (desiredLoop && typeof targetSlider.loopCreate === "function") {
            targetSlider.loopCreate();
          }
          targetSlider.update();
          targetSlider.updateSlidesClasses();

          if (typeof targetSlider.slideToLoop === "function") {
            targetSlider.slideToLoop(activeRealIndex, 0, false);
          } else if (typeof targetSlider.slideTo === "function") {
            targetSlider.slideTo(activeRealIndex, 0, false);
          }

          if (desiredLoop) {
            this.attachLoopFixListener(targetSlider, loopFixAttachedKey);
          }
          if (typeof onComplete === "function") onComplete();
          this.restoreAutoplayState();
        } finally {
          this[inProgressKey] = false;
        }
      }

      setThumbnails() {
        const thumbsSlideContainer = this.thumbnails;
        let thumbOptions = {};
        try {
          thumbOptions = JSON.parse(this.thumbnails.dataset.swiperOptions);
        } catch (err) {
          console.error(err);
        }
        const thumbnailsSlider = new Swiper(thumbsSlideContainer, thumbOptions);
        this.thumbsBaseOptions = {
          loop: !!thumbnailsSlider.params.loop,
          centeredSlides: !!thumbnailsSlider.params.centeredSlides,
          centerInsufficientSlides: !!thumbnailsSlider.params.centerInsufficientSlides,
        };
        this.sliderOptions.thumbs = { swiper: thumbnailsSlider };
        this.bindThumbKeyboardActivation(thumbsSlideContainer);
      }

      // Swiper's thumbs linking navigates on its own internal pointer-driven "tap" event,
      // which a keyboard Enter/Space on a thumb <button> never fires (that only dispatches
      // a plain "click"). Bridge that gap so keyboard activation still moves the main slider.
      // Delegated on the container (rather than bound per-slide) so it keeps working when
      // callers like quick-add.js replace the thumb markup via innerHTML after this runs.
      bindThumbKeyboardActivation(thumbsSlideContainer) {
        thumbsSlideContainer.addEventListener("click", (event) => {
          const thumbSlide = event.target.closest(".swiper-slide");
          if (!thumbSlide || !thumbsSlideContainer.contains(thumbSlide)) return;
          const index = Array.from(thumbsSlideContainer.querySelectorAll(".swiper-slide")).indexOf(thumbSlide);
          if (index >= 0) this.slider?.slideTo(index);
        });
      }

      // Mirrors Swiper's own "swiper-slide-thumb-active" class onto aria-pressed so
      // keyboard/screen-reader users can tell which thumbnail button is selected.
      syncThumbnailPressedState() {
        if (!this.thumbnails) return;
        this.thumbnails.querySelectorAll(".swiper-slide").forEach((thumb) => {
          thumb.setAttribute("aria-pressed", thumb.classList.contains("swiper-slide-thumb-active") ? "true" : "false");
          thumb.removeAttribute("aria-current");
        });
      }

      initializeSliderEventListeners() {
        if (this.isVideoWrapper) {
          this.slider.on("slideChange", () => {
            const activeIndex = this.slider.realIndex !== undefined ? this.slider.realIndex : this.slider.activeIndex;
            this.slides.forEach((slide, index) => {
              if (index === activeIndex || !slide.dataset?.videoWrapper) {
                return;
              }
              const deferredMediaElements = slide.querySelectorAll("deferred-media");
              deferredMediaElements.forEach((deferredMedia) => {
                if (!deferredMedia.hasAttribute("autoplay") && typeof deferredMedia.pauseMedia === "function") {
                  deferredMedia.pauseMedia();
                }
              });
            });
          });
        }
      }

      updateSlides(event) {
        const newSlides = Array.from(event.detail?.slides || []);
        if (!newSlides.length) return;

        if (!this.slider || !this.initialized) {
          const slidesWrapper = this.slideContainer?.querySelector(".swiper-wrapper");
          newSlides.forEach((slide) => slidesWrapper?.appendChild(slide));
          this.syncElements();
          this.initializeSwiper();
          return;
        }

        this.slider.appendSlide(newSlides);
        this.syncElements();
        this.slider.thumbs?.swiper?.update();
        this.slider.update();

        const index = Number.isInteger(event.detail?.index) ? event.detail.index : 0;
        this.slider.slideTo(index);
        this.queueLoopCapacityCheck(50);
      }

      slideToIndex(event) {
        if (!this.slider || !this.initialized) {
          console.warn("SwiperSlider - Slider not initialized");
          return;
        }

        const { index, speed, sliderId } = event.detail || {};

        if (sliderId && this.dataset.sliderId !== sliderId) {
          return;
        }

        if (typeof index === "number" && index >= 0 && index < this.slides.length) {
          let slidesIndex;
          for (let i = 0; i < this.slider.slides.length; i += 1) {
            if (parseInt(this.slider.slides[i].dataset.actualSlideIndex, 10) === index) {
              slidesIndex = i;
              break;
            }
          }
          const slideSpeed = speed !== undefined ? speed : this.sliderOptions.speed;
          this.slider.slideTo(slidesIndex ?? index, slideSpeed);
        } else {
          console.warn("SwiperSlider - Invalid slide index:", index);
        }
      }
    }

    customElements.define("swiper-slider", SwiperSlider);
  }
}
